import { ClassroomType } from '@peekapak/types/DataProtocolTypes';
import { CompletionStatus, ELAStandardsMap } from '@peekapak/types/LessonPlanTypes';
import { getMiddleSchoolUnits, getHighSchoolUnits, MiddleSchoolCard } from '../components/LibraryCard/cardData';
import { getUsersEnableContentTags } from '../GlobalFunctions';

const getIsMenaUser = () => {
  const enabledContentTags = getUsersEnableContentTags();
  return enabledContentTags && enabledContentTags.includes('mena');
};

// Get supplementary units from cardData
const getSupplementaryUnits = (grade: number) => {
  const middleSchoolUnits = getMiddleSchoolUnits() as MiddleSchoolCard[];
  const highSchoolUnits = getHighSchoolUnits() as MiddleSchoolCard[];
  const allUnits = [...middleSchoolUnits, ...highSchoolUnits];
  const isMenaUser = getIsMenaUser();

  // Filter for supplementary units based on filterTags and grade span, and region
  return allUnits.filter((unit) => {
    const isMenaContent = unit.applicationTags?.includes('mena');
    if (isMenaContent && !isMenaUser) return false;
    return unit.filterTags?.includes('supplementary') && unit.gradeSpan.includes(grade);
  });
};

export const getSecondaryCompletion = (completionStatus: ClassroomType['completionStatus'], grade: number) => {
  const middleSchoolUnits = getMiddleSchoolUnits() as MiddleSchoolCard[];
  const highSchoolUnits = getHighSchoolUnits() as MiddleSchoolCard[];

  const allUnits = [...middleSchoolUnits, ...highSchoolUnits];
  const isMenaUser = getIsMenaUser();

  // Filter units by grade span AND region
  const validUnitIDs = allUnits
    .filter((unit) => {
      const isMenaContent = unit.applicationTags?.includes('mena');
      if (isMenaContent && !isMenaUser) return false;
      return unit.gradeSpan.includes(grade);
    })
    .map((u) => u.unit);

  const supplementaryUnitsList = getSupplementaryUnits(grade).map((u) => u.unit);

  return Object.keys(completionStatus)
    .filter((key) => validUnitIDs.includes(key))
    .filter((key) => !supplementaryUnitsList.includes(key))
    .reduce<CompletionStatus>((obj, key) => {
      (obj[key as keyof CompletionStatus] as unknown) = completionStatus[key];
      return obj;
    }, {});
};

export const getSupplementaryCompletion = (completionStatus: ClassroomType['completionStatus'], grade: number) => {
  const supplementaryUnits = getSupplementaryUnits(grade);
  const supplementaryUnitIDs = supplementaryUnits.map((u) => u.unit);

  return Object.keys(completionStatus)
    .filter((key) => supplementaryUnitIDs.includes(key))
    .reduce<CompletionStatus>((obj, key) => {
      (obj[key as keyof CompletionStatus] as unknown) = completionStatus[key];
      return obj;
    }, {});
};

export const getNumModules = (grade: number, supplementaryOnly = false) => {
  const middleSchoolUnits = getMiddleSchoolUnits() as MiddleSchoolCard[];
  const highSchoolUnits = getHighSchoolUnits() as MiddleSchoolCard[];
  const allUnits = [...middleSchoolUnits, ...highSchoolUnits];
  const isMenaUser = getIsMenaUser();
  let units = grade > 8 ? highSchoolUnits : middleSchoolUnits;

  // For supplementary units, check both middle and high school since some units span both
  if (supplementaryOnly) {
    units = allUnits.filter((unit) => {
      const isMenaContent = unit.applicationTags?.includes('mena');
      if (isMenaContent && !isMenaUser) return false;
      return unit.filterTags?.includes('supplementary') && unit.gradeSpan.includes(grade);
    });
  } else {
    // Exclude supplementary units from curriculum count
    units = units.filter((unit) => {
      const isMenaContent = unit.applicationTags?.includes('mena');
      if (isMenaContent && !isMenaUser) return false;
      return !unit.filterTags?.includes('supplementary');
    });
  }

  return units.reduce((acc, curr) => (acc += curr.numModules), 0);
};

export const getNumCompleteModules = (secondaryCompletion: CompletionStatus) => {
  return Object.values(secondaryCompletion).reduce((acc, curr) => {
    for (const lessonCompletion of Object.values(curr)) {
      const moduleStatuses = Object.values(lessonCompletion);
      if (moduleStatuses.length > 0 && moduleStatuses.every((v) => v === 'Completed' || v === 'DeemedCompleted')) {
        acc += 1;
      }
    }
    return acc;
  }, 0);
};

export const getUnitCompletion = (
  secondaryCompletion: CompletionStatus,
  grade: number,
  standardsMap?: ELAStandardsMap,
  supplementaryOnly = false,
) => {
  const middleSchoolUnits = getMiddleSchoolUnits() as MiddleSchoolCard[];
  const highSchoolUnits = getHighSchoolUnits() as MiddleSchoolCard[];
  const isMenaUser = getIsMenaUser();

  // Filter for supplementary or regular units based on flag
  let units = grade > 8 ? highSchoolUnits : middleSchoolUnits;

  if (supplementaryOnly) {
    // Check both middle and high school since some supplementary units span both
    const allUnits = [...middleSchoolUnits, ...highSchoolUnits];
    units = allUnits.filter((unit) => {
      const isMenaContent = unit.applicationTags?.includes('mena');
      if (isMenaContent && !isMenaUser) return false;
      return unit.filterTags?.includes('supplementary') && unit.gradeSpan.includes(grade);
    });
  } else {
    // Exclude supplementary units from curriculum section
    units = units.filter((unit) => {
      const isMenaContent = unit.applicationTags?.includes('mena');
      if (isMenaContent && !isMenaUser) return false;
      return !unit.filterTags?.includes('supplementary');
    });
  }

  // Return all units with their progress (including those with 0% progress)
  return units.map((unit) => {
    const unitCompletion = secondaryCompletion[unit.unit];

    // Get total lessons from standardsMap if available
    let totalLessons = unit.numModules;
    if (standardsMap?.data) {
      const unitData = standardsMap.data[unit.unit];
      if (unitData) {
        totalLessons = Object.keys(unitData).length;
      }
    }

    const completeLessons = unitCompletion
      ? Object.values(unitCompletion).filter((lessonCompletion) => {
          const moduleStatuses = Object.values(lessonCompletion);
          return moduleStatuses.length > 0 && moduleStatuses.every((v) => v === 'Completed' || v === 'DeemedCompleted');
        }).length
      : 0;
    return {
      id: unit.unit,
      displayName: unit.displayName,
      totalModules: totalLessons,
      completeModules: completeLessons,
    };
  });
};

export const getAllApplicableStandards = (standardsMap: ELAStandardsMap, grade: number) => {
  const middleSchoolUnits = getMiddleSchoolUnits() as MiddleSchoolCard[];
  const highSchoolUnits = getHighSchoolUnits() as MiddleSchoolCard[];
  const isMenaUser = getIsMenaUser();

  return Object.entries(standardsMap.data).reduce((acc, [unitId, lessons]) => {
    const unit = [...middleSchoolUnits, ...highSchoolUnits].find((u) => u.unit === unitId);
    if (!unit) return acc;

    // Filter by region
    const isMenaContent = unit.applicationTags?.includes('mena');
    if (isMenaContent && !isMenaUser) return acc;

    // Filter by grade
    if (!unit.gradeSpan.includes(grade)) return acc;

    for (const lesson of Object.values(lessons)) {
      acc.push(...lesson.standards);
    }
    return acc;
  }, [] as string[]);
};

export interface LessonWithStandards {
  unitId: string;
  unitNum: number;
  displayName: string;
  lessonId: string;
  standards: string[];
}
export type LessonsWithStandards = LessonWithStandards[];

export const getCompletedLessonsWithStandards = (
  secondaryCompletion: CompletionStatus,
  standardsMap: ELAStandardsMap,
  grade: number,
) => {
  const middleSchoolUnits = getMiddleSchoolUnits() as MiddleSchoolCard[];
  const highSchoolUnits = getHighSchoolUnits() as MiddleSchoolCard[];
  const isMenaUser = getIsMenaUser();
  const allUnits = [...middleSchoolUnits, ...highSchoolUnits];

  return Object.entries(secondaryCompletion).reduce((acc, [unitId, lessons]) => {
    const unit = allUnits.find((u) => u.unit === unitId);
    if (!unit) return acc;

    const isMenaContent = unit.applicationTags?.includes('mena');
    if (isMenaContent && !isMenaUser) return acc;

    if (!unit.gradeSpan.includes(grade)) return acc;

    for (const [lessonId, value] of Object.entries(lessons)) {
      const lessonData = standardsMap?.data[unitId]?.[lessonId];
      if (
        lessonData &&
        Object.values(value).every((v) => v === 'Completed' || v === 'DeemedCompleted') &&
        Object.values(value).length === lessonData.numberOfModules
      ) {
        acc.push({
          unitId: unitId,
          unitNum: allUnits.map((u) => u.unit).indexOf(unitId),
          lessonId: lessonId,
          displayName: unit.displayName || unitId,
          standards: lessonData.standards,
        });
      }
    }
    return acc;
  }, [] as LessonsWithStandards);
};

export const getIncompleteLessonsWithStandards = (
  secondaryCompletion: CompletionStatus,
  standardsMap: ELAStandardsMap,
  grade: number,
) => {
  const middleSchoolUnits = getMiddleSchoolUnits() as MiddleSchoolCard[];
  const highSchoolUnits = getHighSchoolUnits() as MiddleSchoolCard[];
  const isMenaUser = getIsMenaUser();
  const allUnits = [...middleSchoolUnits, ...highSchoolUnits];

  return Object.entries(standardsMap.data).reduce((acc, [unitId, lessons]) => {
    const unit = allUnits.find((u) => u.unit === unitId);
    if (!unit) return acc;

    const isMenaContent = unit.applicationTags?.includes('mena');
    if (isMenaContent && !isMenaUser) return acc;

    if (!unit.gradeSpan.includes(grade)) return acc;

    for (const [lessonId, value] of Object.entries(lessons)) {
      const lessonData = standardsMap.data[unitId]?.[lessonId];
      if (
        !secondaryCompletion[unitId] ||
        !secondaryCompletion[unitId][lessonId] ||
        Object.values(secondaryCompletion[unitId][lessonId]).length !== value.numberOfModules ||
        Object.values(secondaryCompletion[unitId][lessonId]).every((v) => v !== 'Completed' && v !== 'DeemedCompleted')
      ) {
        acc.push({
          unitId: unitId,
          unitNum: allUnits.map((u) => u.unit).indexOf(unitId),
          lessonId: lessonId,
          displayName: unit.displayName || unitId,
          standards: lessonData?.standards || [],
        });
      }
    }
    return acc;
  }, [] as LessonsWithStandards);
};

export const getUniqueMetStandards = (
  secondaryCompletion: CompletionStatus,
  standardsMap: ELAStandardsMap,
  grade: number,
): string[] => {
  const standardsOnly = getCompletedLessonsWithStandards(secondaryCompletion, standardsMap, grade).map(
    (lesson) => lesson.standards,
  );
  return [...new Set(standardsOnly.flat())];
};

export const getTotalCompletedStandards = (
  secondaryCompletion: CompletionStatus,
  standardsMap: ELAStandardsMap,
  grade: number,
) => getUniqueMetStandards(secondaryCompletion, standardsMap, grade).length;

export const getCompleteAndIncompleteByUnit = (
  completedLessons: LessonsWithStandards,
  incompleteLessons: LessonsWithStandards,
  grade: number,
): {
  id: string;
  displayName: string;
  completeStandards: string[];
  incompleteStandards: string[];
}[] => {
  const middleSchoolUnits = getMiddleSchoolUnits() as MiddleSchoolCard[];
  const highSchoolUnits = getHighSchoolUnits() as MiddleSchoolCard[];
  const isMenaUser = getIsMenaUser();

  const allUnits = [...middleSchoolUnits, ...highSchoolUnits];
  const filteredUnits = allUnits.filter((unit) => {
    const isMenaContent = unit.applicationTags?.includes('mena');
    if (isMenaContent && !isMenaUser) return false;
    return unit.gradeSpan.includes(grade);
  });

  const unitStandardsCompletion = filteredUnits.map((unit) => {
    const unitCompletedStandards = completedLessons
      .filter((lesson) => lesson.unitId === unit.unit)
      .map((unit) => unit.standards)
      .flat();
    const uniqueCompleteStandards = [...new Set(unitCompletedStandards)];
    const unitIncompleteStandards = incompleteLessons
      .filter((lesson) => lesson.unitId === unit.unit)
      .map((unit) => unit.standards)
      .flat();
    const uniqueIncompleteStandards = [...new Set(unitIncompleteStandards)].filter(
      (standard) => !uniqueCompleteStandards.includes(standard),
    );
    return {
      id: unit.unit,
      displayName: unit.displayName,
      completeStandards: uniqueCompleteStandards,
      incompleteStandards: uniqueIncompleteStandards,
    };
  });
  return unitStandardsCompletion;
};
